// @flow
import React, {useState} from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ClickAwayListener,
  Grid,
  Hidden,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';

import {usePage} from '../../../../page/utils';

import {colors} from '../../../../../assets/styles/colors';

import {useMemo} from 'react';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {Link, useHistory} from 'react-router-dom';
import {CheckCircle} from '@material-ui/icons';
import {useEffect} from 'react';

import type {Store, SinglePromotion} from '../../../../../entities';
import {getCustomDomain} from '../../../../../utils/component';
// import SnackBar from '../../../../../elements/SnackBar/SnackBar';
type Props = {
  store?: Store,
  createPromotionSuccess?: boolean,
  promotion?: SinglePromotion,
  resetPromotionState: () => void,
};

export default usePage<Props>(PromotionSuccess, 'PromotionSuccess');

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: colors.grey900,
    textAlign: 'center',
    fontSize: '22px',
    fontWeight: 600,
    [theme.breakpoints.up('md')]: {
      fontSize: '24px',
    },
  },
  subtitle: {
    textAlign: 'center',
    color: colors.grey800,
    fontSize: '16px',
    margin: theme.spacing(2, 0),
  },
  contentWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  cardContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  copyLink: {
    color: '#500BDB',
  },
  containerCopyLink: {
    width: '80%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    cursor: 'default',
    fontSize: '12px',
    color: '#3A3A49',
  },
  wrapperLink: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 1),
    width: '100%',
    background: '#F5F0FF',
    border: '1.25px solid rgba(123, 57, 255, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      width: '90%',
    },
  },
  containerButtonLink: {
    width: '60px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  containerButton: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  successIcon: {
    margin: theme.spacing(2, 0),
    color: colors.success,
  },
}));

function PromotionSuccess(props: Props) {
  const {store, createPromotionSuccess, promotion, resetPromotionState} = props;

  const classes = useStyles();
  const history = useHistory();

  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!createPromotionSuccess || !promotion) {
      history.push('/marketing/promotions');
    }
  }, [createPromotionSuccess, history, promotion]);

  useEffect(() => {
    return () => resetPromotionState();
  }, [resetPromotionState]);

  const promotionLink = useMemo(() => {
    if (!store || !promotion || !promotion.slug) return '';
    // const encodedName = encodeURIComponent(promotion.name);
    return `https://${getCustomDomain(store)}/promo/${
      promotion.slug ? promotion.slug : ''
    }`;
  }, [promotion, store]);

  const copyButton = (
    <ClickAwayListener onClickAway={() => setLinkCopied(false)}>
      <Tooltip
        placement="right"
        arrow
        PopperProps={{
          disablePortal: true,
        }}
        onClose={() => setLinkCopied(false)}
        open={linkCopied}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        leaveDelay={1000}
        title="¡Link copiado!"
      >
        <Button
          className={classes.copyLink}
          disabled={!store}
          onClick={() => {
            const textCopied = store ? promotionLink : '';
            store &&
              navigator &&
              navigator.clipboard &&
              navigator.clipboard
                .writeText(textCopied)
                .then(() => {
                  setLinkCopied(true);
                })
                .catch(e => console.error(e));
          }}
        >
          Copiar
        </Button>
      </Tooltip>
    </ClickAwayListener>
  );

  if (!promotion || !createPromotionSuccess) return <></>;
  return (
    <div className={classes.root}>
      {/* {createPromotionSuccess && (
        <SnackBar mode="success" message="Promoción creada correctamente" />
      )} */}
      <div className={classes.contentWrapper}>
        <Grid container justify="center">
          <Grid item xs={12} sm={6}>
            <div className={classes.card}>
              <div className={classes.cardContent}>
                <CheckCircle fontSize="large" className={classes.successIcon} />
                <Typography className={classes.title}>
                  ¡Promoción creada{' '}
                  <Hidden mdUp>
                    <br />
                  </Hidden>{' '}
                  existosamente!
                </Typography>
                <Typography className={classes.subtitle}>
                  Comparte el link de tu promoción con tus clientes
                </Typography>

                <div className={classes.wrapperLink}>
                  <div className={classes.containerCopyLink}>
                    {promotionLink}
                  </div>
                  <div className={classes.containerButtonLink}>
                    {copyButton}
                  </div>
                </div>

                <div className={classes.containerButton}>
                  <CustomButton
                    fullWidth
                    variant="contained"
                    component={Link}
                    to="/marketing/promotions"
                  >
                    Regresar a Promociones
                  </CustomButton>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

PromotionSuccess.propTypes = {
  store: PropTypes.object,
  createPromotionSuccess: PropTypes.bool,
  promotion: PropTypes.object,
  resetPromotionState: PropTypes.func.isRequired,
};
